<template>
  <div
    class="workspace-page product-membership"
    :class="{ 'product-acquisition': campaignIntent }"
  >
    <router-link
      v-if="returnTo && !campaignIntent"
      class="product-back"
      :to="returnTo"
    >
      ← {{ $t('product.back-to-work') }}
    </router-link>
    <header class="product-membership-hero">
      <div>
        <p class="workspace-eyebrow">
          INDIEFY PRO
        </p><h1>{{ $t(campaignIntent ? `acquisition.${campaignIntent}-title` : `product.pro-${goal}`) }}</h1><p>{{ $t(campaignIntent ? `acquisition.${campaignIntent}-copy` : `product.pro-${goal}-copy`) }}</p>
      </div><div
        v-if="!campaignIntent"
        class="product-membership-orbit"
        aria-hidden="true"
      >
        <span>YOUR MUSIC</span><strong>↗</strong><span>MORE POSSIBILITIES</span>
      </div>
    </header>
    <nav
      v-if="!campaignIntent"
      class="product-view-tabs"
      :aria-label="$t('product.choose-goal')"
    >
      <button
        v-for="item in goals"
        :key="item"
        :class="{ active: goal === item }"
        :aria-pressed="goal === item"
        @click="setGoal(item)"
      >
        {{ $t(`product.goal-${item}`) }}
      </button>
    </nav>
    <div class="product-plan-heading">
      <div v-if="!campaignIntent">
        <h2>{{ $t('product.plan-fit') }}</h2><p>{{ $t('product.plan-fit-copy') }}</p>
      </div><div class="product-period">
        <button
          :class="{ active: yearly }"
          :aria-pressed="yearly"
          @click="yearly = true"
        >
          {{ $t('product.yearly') }}
        </button><button
          :class="{ active: !yearly }"
          :aria-pressed="!yearly"
          @click="yearly = false"
        >
          {{ $t('product.monthly') }}
        </button>
      </div>
    </div>
    <p
      v-if="auth.currentUser && route.query.plan === 'pro'"
      class="product-selected-plan"
      role="status"
      data-test="selected-plan"
    >
      {{ $t('acquisition.selected') }}: Indiefy Pro · {{ $t(yearly ? 'product.yearly' : 'product.monthly') }}
    </p>
    <p
      v-if="route.query.cancelled"
      class="product-message"
      role="status"
    >
      {{ $t('product.checkout-cancelled') }}
    </p><p
      v-if="checkoutError"
      class="product-message"
      role="alert"
    >
      {{ checkoutError }}
    </p>
    <div class="product-plan-grid">
      <article
        v-if="!hideBasic && !campaignIntent"
        class="product-plan"
      >
        <span class="workspace-eyebrow">BASIC</span><h2>{{ $t('product.plan-start') }}</h2><p>{{ $t('product.basic-copy') }}</p><p class="product-price">
          $0 <small>USD</small>
        </p><ul><li>{{ $t('product.basic-limits') }}</li><li>{{ $t('product.basic-growth') }}</li><li>{{ $t('product.basic-distribution') }}</li></ul><router-link
          class="button is-light"
          :to="auth.currentUser ? '/distributions' : '/auth/signup'"
        >
          {{ $t(auth.currentUser ? 'product.continue-basic' : 'product.start-free') }}
        </router-link>
      </article><article class="product-plan product-plan-featured">
        <span
          v-if="!campaignIntent"
          class="workspace-eyebrow"
        >PRO · {{ $t('product.for-growing-artists') }}</span><h2>{{ campaignIntent ? 'Indiefy Pro' : $t('product.plan-grow') }}</h2><p v-if="!campaignIntent">
          {{ $t('product.pro-plan-copy') }}
        </p><p class="product-price">
          ${{ yearly ? '49' : '9.99' }} <small>USD / {{ $t(yearly ? 'general.year' : 'general.month') }}</small>
        </p><small>{{ $t(yearly ? 'product.yearly-total' : 'product.monthly-total') }}</small><ul><li>{{ $t('product.pro-limits') }}</li><li>{{ $t('product.pro-campaign-benefit') }}</li><li>{{ $t('product.pro-fan-benefit') }}</li><li>{{ $t('product.pro-insight-benefit') }}</li><li>{{ $t('product.pro-release-benefit') }}</li></ul><button
          class="button is-primary"
          :disabled="loading"
          :class="{ 'is-loading': loading && selectedPlan === 'pro' }"
          data-test="choose-pro"
          @click="choosePlan('pro')"
        >
          {{ $t(auth.currentUser?.subscription ? 'product.manage-membership' : auth.currentUser && route.query.plan === 'pro' ? 'acquisition.continue' : 'product.choose-pro') }} →
        </button><p class="product-checkout-note">
          {{ $t('product.checkout-review') }}
        </p>
      </article><article
        v-if="!campaignIntent"
        class="product-plan"
      >
        <span class="workspace-eyebrow">ADVANCED</span><h2>{{ $t('product.plan-team') }}</h2><p>{{ $t('product.advanced-copy') }}</p><p class="product-price">
          $199 <small>USD / {{ $t('general.year') }}</small>
        </p><small>{{ $t('product.advanced-total') }}</small><ul><li>{{ $t('product.advanced-limits') }}</li><li>{{ $t('product.advanced-growth') }}</li><li>{{ $t('product.advanced-yearly') }}</li></ul><button
          class="button is-light"
          :disabled="loading"
          @click="choosePlan('advanced')"
        >
          {{ $t(auth.currentUser?.subscription ? 'product.manage-membership' : 'product.choose-advanced') }}
        </button>
      </article>
    </div>
    <router-link
      v-if="campaignIntent"
      class="product-compare-plans"
      :to="{ path: '/upgrade/', query: { lang: locale } }"
    >
      {{ $t('acquisition.compare') }} →
    </router-link>
    <section class="product-panel product-plan-details">
      <h2>{{ $t('product.before-you-choose') }}</h2><details><summary>{{ $t('product.about-results') }}</summary><p>{{ $t('product.results-copy') }}</p></details><details><summary>{{ $t('product.about-billing') }}</summary><p>{{ $t('settings.faq-billing-answer') }}</p><p>{{ $t('product.plan-change-copy') }}</p></details><details>
        <summary>{{ $t('product.about-royalties') }}</summary><p>{{ $t('product.royalty-copy') }}</p><router-link to="/royalties">
          {{ $t('navigation.royalties') }} →
        </router-link>
      </details><a
        href="https://help.indiefy.net"
        target="_blank"
        rel="noopener noreferrer"
      >{{ $t('settings.contact-us') }} ↗</a>
    </section>
  </div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/store/auth';
import userService from '@/services/userService';
import { startProCheckout } from '@/utils/proCheckout';
const route = useRoute(); const router = useRouter(); const { t, locale } = useI18n(); const auth = useAuthStore();
const campaignIntent = computed(() => ['spotify', 'distribution'].includes(route.query.intent) ? route.query.intent : /^indiefy_pro_search_/.test(route.query.utm_campaign || '') ? 'distribution' : '');
const goals = ['release','campaigns','fans','insights'];
const goal = computed(() => goals.includes(route.query.goal) ? route.query.goal : 'release');
const yearly = ref(route.query.billing !== 'monthly'); const hideBasic = ref(false); const loading = ref(false); const checkoutError = ref(''); const selectedPlan = ref('');
const returnTo = computed(() => typeof route.query.returnTo === 'string' && route.query.returnTo.startsWith('/') && !route.query.returnTo.startsWith('//') ? route.query.returnTo : auth.currentUser ? '/tools' : '/');
function setGoal(value) { router.replace({ query: { ...route.query, goal: value } }); }
async function choosePlan(plan) {
  if (loading.value) return;
  if (auth.currentUser?.subscription) { router.push('/account/subscription'); return; }
  if (!auth.currentUser) { router.push({ path: '/auth/signup', query: { lang: locale.value, redirect: router.resolve({ path: '/upgrade/', query: { ...route.query, plan, billing: plan === 'advanced' || yearly.value ? 'yearly' : 'monthly' } }).fullPath } }); return; }
  loading.value = true; selectedPlan.value = plan; checkoutError.value = '';
  try {
    await startProCheckout({ plan: plan === 'advanced' ? 'advanced' : yearly.value ? 'yearly' : 'monthly',
      from: route.query.from || `pro-${goal.value}`, value: plan === 'advanced' ? 199 : yearly.value ? 49 : 9.99 });
  } catch (_) { checkoutError.value = t('product.checkout-error'); } finally { loading.value = false; }
}
onMounted(async () => { try { hideBasic.value = !!(await userService.getLocationSettings()).hide_basic; } catch { hideBasic.value = false; } });
</script>
